import {
    signUp,
    confirmSignUp,
    resendSignUpCode,
    signIn,
    confirmSignIn,
    signOut,
    signInWithRedirect,
    getCurrentUser,
    fetchUserAttributes,
    fetchAuthSession,
    updateUserAttributes,
} from 'aws-amplify/auth';
import type { AuthService, SignInResult, SignUpParams, SupplierUser, UserRole } from '../types';
import { PendingApprovalError } from '../types';
import { ensureAmplifyConfigured } from './amplifyConfig';
import { getAwsConfig } from '../../config/appConfig';
import { getDevRoles } from '../../auth/devRoles';

const KNOWN_ROLES: UserRole[] = ['admin', 'supplier-control', 'supplier-insights'];

/**
 * Read employee role groups from the ID token's `cognito:groups` claim.
 * Password suppliers have no employee group, so this returns []. Employees
 * federated via Midway carry `supplier-control` and/or `supplier-insights`.
 */
async function currentRoles(): Promise<UserRole[]> {
    // Dev-only "View as" override (localStorage / VITE_DEV_ROLES) for previewing
    // the app as different user types. Inert in production builds.
    const dev = getDevRoles();
    if (dev !== null) return dev;

    try {
        const session = await fetchAuthSession();
        const claim = session.tokens?.idToken?.payload['cognito:groups'];
        const groups = Array.isArray(claim) ? claim.map(String) : [];
        return KNOWN_ROLES.filter((r) => groups.includes(r));
    } catch {
        return [];
    }
}

// Provider prefixes Cognito puts on federated usernames (e.g. "Midway_alias").
// Mirrors the service-side detection in auth/directory.py.
const FEDERATED_PREFIXES = ['midway', 'federate'];

/**
 * Whether the signed-in user is a federated (Midway SSO) employee vs a native
 * password supplier. Federated users carry an `identities` claim and/or a
 * provider-prefixed `cognito:username` (e.g. "Midway_alias"). This drives the
 * no-role access decision: an employee in no role group is denied, a supplier
 * falls back to the Connect area.
 */
async function currentIsEmployee(): Promise<boolean> {
    try {
        const session = await fetchAuthSession();
        const payload = session.tokens?.idToken?.payload ?? {};
        if (payload.identities) return true;
        const username = String(payload['cognito:username'] ?? '');
        const prefix = username.includes('_') ? username.split('_', 1)[0].toLowerCase() : '';
        return FEDERATED_PREFIXES.includes(prefix);
    } catch {
        return false;
    }
}

/**
 * Record the signed-in user's File Drop identity-pool id on their Cognito
 * profile (`custom:identityPoolId`) once, if not already set. Team-scoped
 * reporting sums each teammate's File Drop counter by this id, so the backend
 * needs it discoverable per login. Best-effort: a failure never blocks sign-in
 * (reporting simply falls back to the viewer's own id until it's recorded).
 */
async function stampIdentityPoolId(): Promise<void> {
    try {
        const { identityId } = await fetchAuthSession();
        const attrs = await fetchUserAttributes();

        // Derive the email domain (lowercased) — the grouping key the approval
        // team picker uses. Backfills existing logins that predate the field.
        const email = attrs.email ?? '';
        const domain = email.includes('@') ? email.split('@').pop()!.toLowerCase() : '';

        const next: Record<string, string> = {};
        if (identityId && attrs['custom:identityPoolId'] !== identityId) {
            next['custom:identityPoolId'] = identityId;
        }
        if (domain && attrs['custom:domain'] !== domain) {
            next['custom:domain'] = domain;
        }
        if (Object.keys(next).length === 0) return;
        await updateUserAttributes({ userAttributes: next });
    } catch {
        // Non-fatal: never block sign-in on a profile-stamp failure.
    }
}

/**
 * Read the brand role ('MASTER' | 'MEMBER') from the ID token's `role` claim
 * (injected by the PreTokenGeneration trigger from `custom:role`). Undefined
 * when absent, which the UI treats as a non-master (least privilege).
 */
async function currentBrandRole(): Promise<'MASTER' | 'MEMBER' | undefined> {
    try {
        const session = await fetchAuthSession();
        const claim = String(session.tokens?.idToken?.payload.role ?? '')
            .trim()
            .toUpperCase();
        return claim === 'MASTER' || claim === 'MEMBER' ? claim : undefined;
    } catch {
        return undefined;
    }
}

async function currentSupplier(fallbackEmail = ''): Promise<SupplierUser> {
    const attributes = await fetchUserAttributes();
    return {
        email: attributes.email ?? fallbackEmail,
        company: attributes['custom:company'] ?? '',
        // Profile photo from the IdP (federated employees only); may be absent.
        picture: attributes.picture || undefined,
        roles: await currentRoles(),
        isEmployee: await currentIsEmployee(),
        brandRole: await currentBrandRole(),
    };
}

/**
 * Real auth backed by Cognito via AWS Amplify. Uses the SRP flow (Amplify's
 * default) so the password is never sent in plaintext.
 */
export const awsAuthService: AuthService = {
    async signUp({
        email,
        password,
        company,
        country,
        contactName,
        phone,
        teamHint,
    }: SignUpParams): Promise<void> {
        ensureAmplifyConfigured();
        const userAttributes: Record<string, string> = {
            email,
            'custom:company': company,
            'custom:country': country,
        };
        // Optional fields: only send when provided (Cognito rejects empty
        // strings for standard attributes). `name` is the built-in standard
        // attribute — no schema change needed.
        if (contactName) {
            userAttributes.name = contactName;
        }
        if (phone) {
            userAttributes.phone_number = phone;
        }
        // Optional, non-authoritative team hint for the approver.
        if (teamHint) {
            userAttributes['custom:teamHint'] = teamHint;
        }
        await signUp({
            username: email,
            password,
            options: { userAttributes },
        });
    },

    async confirmSignUp(email: string, code: string): Promise<void> {
        ensureAmplifyConfigured();
        await confirmSignUp({ username: email, confirmationCode: code });
    },

    async resendSignUpCode(email: string): Promise<void> {
        ensureAmplifyConfigured();
        await resendSignUpCode({ username: email });
    },

    async signIn(email: string, password: string): Promise<SignInResult> {
        ensureAmplifyConfigured();
        // Clear any lingering session so a fresh sign-in always applies.
        try {
            await signOut();
        } catch {
            // No active session; ignore.
        }

        try {
            const { isSignedIn, nextStep } = await signIn({ username: email, password });
            if (isSignedIn) {
                await stampIdentityPoolId();
                return { status: 'SIGNED_IN', user: await currentSupplier(email) };
            }
            // Email MFA: Cognito has emailed a code; the UI collects it next.
            if (
                nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE' ||
                nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_EMAIL_SETUP'
            ) {
                return { status: 'EMAIL_MFA_REQUIRED' };
            }
            // Account exists but email isn't verified yet. Route the user to the
            // confirm-email screen instead of showing a raw step error.
            if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
                return { status: 'CONFIRMATION_REQUIRED' };
            }
            throw new Error(
                `Unsupported sign-in step (${nextStep.signInStep}). This prototype supports password + email MFA only.`,
            );
        } catch (e) {
            // Disabled accounts are pending admin approval.
            const name = e instanceof Error ? e.name : '';
            const msg = e instanceof Error ? e.message : '';
            if (name === 'UserDisabledException' || /disabled/i.test(msg)) {
                throw new PendingApprovalError(
                    'Your account is awaiting approval. You will be able to sign in once an administrator approves it.',
                );
            }
            throw e;
        }
    },

    async confirmSignIn(code: string, email = ''): Promise<SupplierUser> {
        ensureAmplifyConfigured();
        const { isSignedIn } = await confirmSignIn({ challengeResponse: code });
        if (!isSignedIn) {
            throw new Error('Could not complete sign-in. Please try again.');
        }
        await stampIdentityPoolId();
        // Use the email the supplier signed in with as a fallback, since
        // fetchUserAttributes may not return email immediately after MFA.
        return currentSupplier(email);
    },

    async resendMfaCode(email: string, password: string): Promise<void> {
        ensureAmplifyConfigured();
        // Cognito has no standalone "resend OTP" for an in-flight challenge, so
        // re-initiate sign-in to make it email a fresh code. Clear any partial
        // session first so signIn starts clean.
        try {
            await signOut();
        } catch {
            // No active session; ignore.
        }
        const { nextStep } = await signIn({ username: email, password });
        if (
            nextStep.signInStep !== 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE' &&
            nextStep.signInStep !== 'CONTINUE_SIGN_IN_WITH_EMAIL_SETUP'
        ) {
            throw new Error('Could not resend the code. Please sign in again.');
        }
    },

    async resetPassword(email: string): Promise<void> {
        // Custom self-service reset (keeps email OTP MFA at sign-in): the API
        // emails a single-use code. Always resolves; never reveals whether the
        // account exists.
        const base = getAwsConfig().asnApiEndpoint;
        if (!base) throw new Error('API endpoint is not configured.');
        const res = await fetch(`${base.replace(/\/$/, '')}/v1/auth/forgot-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
        });
        if (!res.ok) {
            throw new Error('Could not start the password reset. Please try again.');
        }
    },

    async confirmResetPassword(email: string, code: string, newPassword: string): Promise<void> {
        const base = getAwsConfig().asnApiEndpoint;
        if (!base) throw new Error('API endpoint is not configured.');
        const res = await fetch(`${base.replace(/\/$/, '')}/v1/auth/reset-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, code, newPassword }),
        });
        if (!res.ok) {
            let detail = 'Could not reset your password.';
            try {
                const body = await res.json();
                if (body && typeof body.detail === 'string') detail = body.detail;
            } catch {
                // keep default
            }
            throw new Error(detail);
        }
    },

    async signInWithSso(): Promise<void> {
        ensureAmplifyConfigured();
        // If a session already exists, don't re-initiate the redirect (Amplify
        // throws "There is already a signed in user"). The AuthContext Hub
        // listener will surface the current user instead.
        try {
            await getCurrentUser();
            return; // already signed in
        } catch {
            // Not signed in; proceed with the Hosted-UI redirect.
        }
        // Launches the Cognito Hosted UI for the Midway IdP; on return, the
        // AuthContext Hub listener resolves the session (and roles).
        await signInWithRedirect({ provider: { custom: 'Midway' } });
    },

    async signOut(): Promise<void> {
        ensureAmplifyConfigured();
        await signOut();
    },

    async getCurrentUser(): Promise<SupplierUser | null> {
        ensureAmplifyConfigured();
        try {
            await getCurrentUser();
            return await currentSupplier();
        } catch {
            return null;
        }
    },

    async getMyCredentials(): Promise<{ clientId: string; clientSecret: string }> {
        ensureAmplifyConfigured();
        const base = getAwsConfig().asnApiEndpoint;
        if (!base) throw new Error('API endpoint is not configured.');
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        if (!token) throw new Error('Not authenticated.');

        const res = await fetch(`${base.replace(/\/$/, '')}/v1/auth/my-credentials`, {
            headers: { Authorization: token },
        });
        if (res.status === 404) {
            throw new Error(
                'No API credentials yet. They are created when an administrator approves your account.',
            );
        }
        if (!res.ok) {
            throw new Error(`Could not load credentials: ${res.status} ${res.statusText}`);
        }
        return (await res.json()) as { clientId: string; clientSecret: string };
    },
};

export default awsAuthService;
